import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Redirect } from "react-router-dom";
import { useCookies } from "react-cookie";
import { ACTIVE_OFFICERS_QUERY } from "../Queries/ActiveOfficersQuery";
import { useLazyQuery, useQuery } from "@apollo/client";
import { SPECIFIC_SETUP_LISTS_QUERY } from "../Queries/SetupListsQuery";
import {
  isSoftTokenDisabled,
  localGetData,
  localStoreData,
  parseObjectArrayToIdArray,
  parseIncidentSubscriptionMessage,
} from "../utils/utils";
import { GET_TICKET_ALLOCATION } from "../Queries/GetTicketAllocation";
import { GET_NARRATIVE_TEMPLATES } from "../Queries/GetTemplateNarratives";
import * as Sentry from "@sentry/react";
import { FLIPPER_ENABLED } from "../Queries/FlipperEnabled";
import { getBuildMeta } from "../buildMeta.js";
import useSessionLogOut from "../CustomHooks/useSessionLogOut";
import { logOutSession } from "../utils/logout";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";

import moment from "moment";

export const UserContext = createContext();

const UserContextProvider = props => {
  const { onlineState, subscriptionServer, timestamp } = props;
  const [user, setUser] = useState({
    id: null,
    firstName: "",
    lastName: "",
    middleName: "",
    badge: "",
    homeOriId: null,
    homeOriName: "",
    homeOriAbbreviation: "",
    stateIdNumber: "",
    selectedOris: null,
    isAuthenticated: false,
    errorMessage: "",
    online: true,
    roles: [],
    roleNames: [],
    onDuty: false,
    selectedOrisObjs: [],
    ori: {},
    officer: {},
    startingTicketNumber: null,
    endingTicketNumber: null,
  });

  const [cookies, setCookie] = useCookies();
  const [nightMode, setNightMode] = useState(false);
  const [tokenDisabled, setTokenDisabled] = useState(true);
  const lastTicketAllocationRequestRef = useRef(null);
  const [
    getActiveOfficers,
    {
      loading: activeOfficersLoading,
      error: activeOfficersError,
      data: activeOfficersData,
    },
  ] = useLazyQuery(ACTIVE_OFFICERS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
  });

  const [dispatched, setDispatched] = useState({
    value: false,
    incidentId: null,
  });

  const isTesting = import.meta.env.VITE_CLIENT_URL?.includes("testing");

  const alertDispatch = useCallback(
    m => {
      const { incidentId, originatorId, source, app } =
        parseIncidentSubscriptionMessage(m);
      const emissionSource = source ?? app;
      const selfDispatch = user?.id?.toString() === originatorId?.toString();
      if (selfDispatch && emissionSource === "mobile") {
        return null;
      } else {
        return setDispatched({ value: true, incidentId });
      }
    },
    [user?.id],
  );

  useEffect(() => {
    if (user?.id && user?.onDuty && onlineState) {
      subscriptionServer.subscribeToDispatchForUser(user?.id, alertDispatch);
    } else if (user?.id && !user?.onDuty && onlineState) {
      subscriptionServer.unsubscribeToDispatchForUser(user?.id);
    }
  }, [user?.id, user?.onDuty, subscriptionServer, alertDispatch, onlineState]);

  const [getTicketAllocation] = useLazyQuery(GET_TICKET_ALLOCATION, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: res => {
      if (res) {
        const responseKeys = Object.keys(res);
        const responseKey = responseKeys[0];
        const data = responseKey ? res[responseKey] : null;
        const hasStartingTicketNumber =
          data?.startingTicketNumber !== null &&
          data?.startingTicketNumber !== undefined;
        const hasEndingTicketNumber =
          data?.endingTicketNumber !== null &&
          data?.endingTicketNumber !== undefined;
        if (!data || !hasStartingTicketNumber || !hasEndingTicketNumber) {
          console.warn("Ticket allocation query returned invalid data", {
            responseKey,
            responseKeys,
            res,
            data,
          });
          Sentry.captureException(
            new Error("Invalid ticket allocation payload"),
            {
              extra: {
                operation: "GET_TICKET_ALLOCATION",
                operationName: "GET_TICKET_ALLOCATION",
                requestVariables: lastTicketAllocationRequestRef.current,
                responseKey,
                responseKeys,
                response: res,
                data,
              },
            },
          );
          return;
        }
        setUser(prevState => {
          return {
            ...prevState,
            startingTicketNumber: data.startingTicketNumber,
            endingTicketNumber: data.endingTicketNumber,
          };
        });
      }
    },
  });

  const { refetch: getNarrativeTemplates } = useQuery(GET_NARRATIVE_TEMPLATES, {
    skip: true,
    variables: { originatorId: user?.id },
  });

  const checkTicketAllocation = useCallback(() => {
    if (
      user?.id &&
      user?.roleNames?.includes("Eticket_User") &&
      (user?.endingTicketNumber - user?.startingTicketNumber <
        MIN_NUMBERS_AVAILABLE ||
        user?.startingTicketNumber === null ||
        user?.startingTicketNumber === undefined)
    ) {
      console.log("Getting more tickets");
      lastTicketAllocationRequestRef.current = {
        officerId: user?.id,
        originatorId: user?.id,
      };
      getTicketAllocation({
        variables: { officerId: user?.id, originatorId: user?.id },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
    // eslint-disable-next-line
  }, [
    user?.endingTicketNumber,
    user?.id,
    user?.roleNames,
    user?.startingTicketNumber,
  ]);

  useEffect(() => {
    // console.log('offline starting ticket number has changed:', user?.startingTicketNumber)
  }, [user?.startingTicketNumber]);

  const toggleNightMode = () => {
    setNightMode(!nightMode);
    setCookie(`mp-userId-${user?.id}-night-mode`, !nightMode);
  };
  const [showBulletinDrawer, setShowBulletinDrawer] = useState(false);
  const toggleBulletinDrawer = () => {
    setShowBulletinDrawer(!showBulletinDrawer);
  };

  useEffect(() => {
    setNightMode(
      cookies[`mp-userId-${user?.id}-night-mode`] === "true" ? true : false,
    );
  }, [user?.id, cookies]);

  const responseErrorHandler = data => {
    // Payload-only callbacks log errors. Apollo owns session expiration because
    // it has the request's captured token, which an onCompleted payload lacks.
    const errors = [
      ...(Array.isArray(data?.errors) ? data.errors : []),
      ...Object.values(data || {}).flatMap(result => Array.isArray(result?.errors) ? result.errors : []),
    ];
    errors.forEach(error => console.error(error));
    return null;
  };

  const [
    getSpecificSetup,
    { loading: specificSetupLoading, data: specificSetupData },
  ] = useLazyQuery(SPECIFIC_SETUP_LISTS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: { requestPolicy: "no-cache" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        // console.log('Completed the getSpecificSetup query')
        let allKeys = Object.keys(data.setupLists);
        let usefulKey = null;
        let i = 0;
        do {
          if (
            allKeys[i] &&
            allKeys[i] !== "__typename" &&
            allKeys[i]?.length > 0
          ) {
            if (data.setupLists[allKeys[i]]?.[0]?.oriId) {
              usefulKey = i;
              i = allKeys.length;
            } else {
              i++;
            }
          } else {
            i++;
          }
        } while (i < allKeys.length);
        let oriId = data?.setupLists[allKeys[usefulKey]]?.[0]?.oriId;
        oriId && localStoreData(data, `specific-setup-${oriId}`);
      }
    },
  });

  const [getOris] = useLazyQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: { requestPolicy: "no-cache" },
    onCompleted: data => {
      const disabled = isSoftTokenDisabled(data?.getOris);
      setTokenDisabled(disabled);
    },
  });
  useEffect(() => {
    if (user?.roles && user?.roles?.length > 0) {
      const roleNames = user.roles.map(role => role?.name);
      setUser(prevState => {
        return { ...prevState, roleNames: roleNames };
      });
    }
    if (user?.isAuthenticated) {
      getOris();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roles, user?.isAuthenticated]);

  useEffect(() => {
    if (user?.roleNames?.includes("Eticket_User") && user?.id) {
      // console.log('Firing getTicketAllocation with user id', user?.id)
      lastTicketAllocationRequestRef.current = {
        officerId: user?.id,
        originatorId: user?.id,
      };
      getTicketAllocation({
        variables: { officerId: user?.id, originatorId: user?.id },
      });
    }
  }, [user?.roleNames, user?.id, getTicketAllocation]);

  const MIN_NUMBERS_AVAILABLE = 2;

  const incrementOfflineTicketNumber = ticketNumber => {
    if (ticketNumber < user?.endingTicketNumber) {
      setUser(prevState => {
        return { ...prevState, startingTicketNumber: ticketNumber + 1 };
      });
    } else {
      setUser(prevState => {
        return {
          ...prevState,
          startingTicketNumber: null,
          endingTicketNumber: null,
        };
      });
    }
  };

  useEffect(() => {
    if (user?.selectedOrisObjs?.length > 0) {
      user?.selectedOrisObjs.forEach(sOri => {
        let oriId = sOri?.id;
        if (localGetData(`specific-setup-${oriId}`)) {
          // console.log('Found setup in localStorage')
          const setup = localGetData(`specific-setup-${oriId}`);
          const now = moment();
          const age = now.diff(setup.timestamp, "hours");
          if (age > 8 && onlineState) {
            // console.log('Specific-setup for ORI ${oriId} was too old')
            localStorage.removeItem(`specific-setup-${oriId}`);
            !specificSetupLoading &&
              getSpecificSetup({ variables: { oriId: oriId } });
          } else {
            // console.log(`Using localStorage specific-setup for ORI ${oriId} in state`)
          }
        } else {
          // console.log(`Did not find specific-setup for ORI ${oriId} in localStorage. Fetching it now`)
          !specificSetupLoading &&
            getSpecificSetup({ variables: { oriId: oriId } });
        }
      });
    } else {
      // console.log(`The user has no selectedOrisObjs`)
    }
  }, [
    user?.selectedOrisObjs,
    specificSetupLoading,
    specificSetupData,
    onlineState,
    getSpecificSetup,
  ]);

  useEffect(() => {
    if (user?.homeOriId && user?.id) {
      const oriIds = localGetData(`${user?.id}-selectedOris`);

      if (oriIds) {
        setUser(prevState => {
          return { ...prevState, selectedOrisObjs: oriIds };
        });
      } else {
        setUser(prevState => {
          return {
            ...prevState,
            selectedOrisObjs: [
              {
                abbreviation: user?.homeOriAbbreviation,
                id: user?.homeOriId.toString(),
                oriName: user?.homeOriName,
              },
            ],
          };
        });
      }
    }
  }, [user?.homeOriAbbreviation, user?.homeOriId, user?.homeOriName, user?.id]);

  useEffect(() => {
    if (user?.id && user?.homeOriId) {
      const { id, homeOriId } = user;
      const narrativeTemplate = localGetData("narrative-templates-mp");
      if (narrativeTemplate) {
        if (narrativeTemplate[homeOriId]) {
          // console.log('Found a result with the homeOriId key:', result)
        } else {
          // console.log('Found templates but not for the homeOriId')
          // const response = await getNarrativeTemplates({ oriId: homeOriId, originatorId: id })
          getNarrativeTemplates({ oriId: homeOriId, originatorId: id })
            .then(response => {
              let mergedResults = {
                ...narrativeTemplate,
                ...{ [homeOriId]: response.data.getNarrativeTemplates },
              };
              localStoreData(mergedResults, "narrative-templates-mp");
            })
            .catch(error => console.error(error));
        }
      } else {
        // const response = await getNarrativeTemplates({ oriId: homeOriId, originatorId: id })
        getNarrativeTemplates({ oriId: homeOriId, originatorId: id })
          .then(response => {
            localStoreData(
              { [homeOriId]: response.data.getNarrativeTemplates },
              "narrative-templates-mp",
            );
          })
          .catch(error => console.error(error));
      }
    }

    // eslint-disable-next-line
  }, [user?.homeOriId, user?.id]);

  useEffect(() => {
    if (onlineState) {
      checkTicketAllocation();
    }
  }, [checkTicketAllocation, onlineState]);

  const updateAuthentication = () => {
    setUser(prevState => {
      return { ...prevState, isAuthenticated: true };
    });
  };

  const updateErrorMessage = str => {
    setUser(prevState => {
      return { ...prevState, errorMessage: str };
    });
  };

  const sessionToken = localGetData("mp_token") || null;
  const logOut = useCallback((reason = "USER_REQUESTED", options = {}) => {
    if (!logOutSession(reason, sessionToken, options)) return false;
    setUser(null);
    // PrivateRoute handles navigation after setUser(null). Keep the legacy
    // truthy return value for cleanup callers; a stale callback returns false.
    return <Redirect to="/mp" />;
  }, [sessionToken]);

  const clientTimeout = +user?.inactivityTimeout || 1800;
  useSessionLogOut(timestamp, clientTimeout, logOut, user?.isAuthenticated);

  const updateUserInformation = userObj => {
    const {
      badge,
      firstName,
      lastName,
      middleName,
      userId,
      roles,
      officer,
      ori,
      secureLocation,
      inactivityTimeout,
    } = userObj;
    const {
      dispatchedOris,
      editableOris,
      mutualAidOris,
      secondarySiblings,
      id: oriId,
      oriName,
      abbreviation: oriAbbreviation,
    } = ori;
    const dispatchedOriIds = parseObjectArrayToIdArray(dispatchedOris);
    const editableOriIds = parseObjectArrayToIdArray(editableOris);
    const mutualAidOriIds = parseObjectArrayToIdArray(mutualAidOris);
    const secondarySiblingIds = parseObjectArrayToIdArray(secondarySiblings);
    setUser(prevState => {
      return {
        ...prevState,
        id: parseInt(userId),
        online: true,
        firstName: firstName,
        lastName: lastName,
        middleName: middleName,
        badge: badge,
        homeOriId: parseInt(oriId),
        homeOriName: oriName,
        homeOriAbbreviation: oriAbbreviation,
        selectedOris: oriId.toString(),
        roles: roles,
        stateIdNumber: officer?.deptAttribute?.stateIdNumber,
        status: officer?.deptAttribute?.status,
        team: officer?.deptAttribute?.team || null,
        onDuty: officer?.deptAttribute?.onDuty,
        dispatchedOriIds: dispatchedOriIds,
        editableOriIds: editableOriIds,
        mutualAidOriIds: mutualAidOriIds,
        secondarySiblingIds: secondarySiblingIds,
        ori: ori,
        officer: officer,
        secureLocation: secureLocation,
        inactivityTimeout: inactivityTimeout,
      };
    });
  };
  const toggleOri = oriObject => {
    const { id, abbreviation, agencyType, oriName } = oriObject;
    setUser(prevState => {
      return {
        ...prevState,
        homeOriId: parseInt(id),
        homeOriName: oriName,
        homeOriAbbreviation: abbreviation,
        homeAgencyType: agencyType,
      };
    });
  };

  const updateSelectedOris = oris => {
    setUser(prevState => {
      setCookie("user", { ...prevState, selectedOris: oris }, { path: "/" });
      return { ...prevState, selectedOris: oris };
    });
  };

  //allOfficers is formatted for use in the CustomAutoComplete component, not a dropdown
  const [allOfficers, setAllOfficers] = useState([]);
  useEffect(() => {
    if (
      !activeOfficersData &&
      !activeOfficersLoading &&
      user?.homeOriId &&
      user?.id
    ) {
      getActiveOfficers({
        variables: {
          oriIds: user?.homeOriId.toString(),
          originatorId: user?.id,
        },
      });
    }
  }, [
    activeOfficersLoading,
    activeOfficersError,
    activeOfficersData,
    user,
    getActiveOfficers,
  ]);

  useEffect(() => {
    if (activeOfficersData) {
      setAllOfficers(activeOfficersData.activeOfficers);
    }
  }, [activeOfficersData]);

  useEffect(() => {
    const buildDate =
      getBuildMeta().buildDate || "Unable to determine the build date";
    if (user?.id && user?.homeOriId) {
      Sentry.setUser({
        id: user?.id,
        oriId: user?.homeOriId,
        build: buildDate,
      });
    } else {
      Sentry.setUser({ build: buildDate });
    }
  }, [user?.homeOriId, user?.id]);

  const [swFlipper, { data }] = useLazyQuery(FLIPPER_ENABLED);

  useEffect(() => {
    if (user?.id && user?.homeOriId) {
      // console.log('firing flipper for user S&W')
      swFlipper({
        variables: {
          originatorId: user?.id,
          feature: "size-and-weight",
          oriId: user?.homeOriId,
        },
      });
    }
  }, [swFlipper, user?.id, user?.homeOriId]);

  const sizeAndWeightEnabledForUser = useMemo(
    () => data?.[Object.keys(data)[0]]?.enabled,
    [data],
  );

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        toggleOri,
        updateSelectedOris,
        updateUserInformation,
        updateAuthentication,
        logOut,
        updateErrorMessage,
        nightMode,
        setNightMode,
        toggleNightMode,
        getActiveOfficers,
        activeOfficersData,
        activeOfficersError,
        activeOfficersLoading,
        allOfficers,
        responseErrorHandler,
        incrementOfflineTicketNumber,
        onlineState,
        showBulletinDrawer,
        toggleBulletinDrawer,
        dispatched,
        setDispatched,
        sizeAndWeightEnabledForUser,
        isTesting,
        tokenDisabled,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
